import React, {useState} from "react";

import {FaTriangleExclamation} from "react-icons/fa6";
import {TaskType} from "../../../Types/Task";
import {ToCNode} from "../../../Types/ToCNode";

import "../../../style/PriorityTableStyles/taskstable.css";
import {useLogMetric} from "../../../hooks/useLogMetric";

const TriangleIcon = FaTriangleExclamation as React.ElementType;


// Recursive Tree Node component
function ToCTreeNode({node, task, isMainBranch, logMetric}: { node: ToCNode, task: TaskType, isMainBranch: boolean, logMetric: (action: string) => void }) {
    const [isOpen, setIsOpen] = useState(isMainBranch);
    const hasChildren = node && node.Children && node.Children.length > 0;

    const toggleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) {
            const nextIsOpen = !isOpen;
            setIsOpen(nextIsOpen);
            logMetric(nextIsOpen ? "Expanded" : "Collapsed");
        }
    };

    return (
        <div>
            <div
                onClick={toggleOpen}
                className={`flex items-center gap-2 py-1 ${hasChildren
                    ? 'cursor-pointer hover:text-grey-600 transition-colors duration-200'
                    : 'cursor-default'}`}
            >
                {isMainBranch && <span className="w-3">
                                    {task.Priority >= 150 && <TriangleIcon
                                        className="priority-icon"/>
                                    }
                                </span>
                }
                {!isMainBranch && node.Priority && node.Priority >= 150 &&
                    <TriangleIcon
                        className="priority-icon-small"/>}
                <span className="w-4">
                    {hasChildren && (
                        <button
                            className="flex items-center justify-center w-5 h-5 rounded transition-all duration-200 hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                            aria-label={isOpen ? 'Collapse' : 'Expand'}
                            tabIndex={-1}
                        >
                            <svg
                                className={`w-3 h-3 text-blue-500 transform transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    )}
                </span>
                <span className="font-medium text-gray-800">
                    {isMainBranch ?
                        <>
                            {task.DocumentTitle}
                            {task.DocumentId ? (
                                <> ({task.DocumentId.split("-")[0]})</>
                            ) : null}
                        </> :
                        <>
                            {node.Title}
                        </>
                    }

                </span>
            </div>
            {hasChildren && isOpen && (
                <div className="mt-2 ml-7 animate-fadeIn">
                    <ul className="space-y-1.5 pl-4">
                        {node.Children.map((child) => (
                            <li key={child.Id}>
                                <ToCTreeNode node={child} task={task} isMainBranch={false} logMetric={logMetric}/>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default function TableTitleTree({task}: { task: TaskType }) {
    const logMetricHook = useLogMetric();

    const logMetric = (action: string) => {
        logMetricHook({category: "TaskTree", subCategory: action, message: `User ${action.toLowerCase()} task tree node`, workItemId: task.WorkItemId || "", userRole: task.Role || ""});
    };

    return (
        <div className="w-full">
            <ToCTreeNode node={task.ToC} task={task} isMainBranch={true} logMetric={logMetric}/>
        </div>
    );
}
